import { infer as Infer, z } from 'zod';

const percentSchema = z
  .number('Wartość procentowa jest wymagana')
  .int('Wartość procentowa musi być liczbą całkowitą')
  .min(0, 'Wartość procentowa nie może być mniejsza niż 0')
  .max(100, 'Wartość procentowa nie może być większa niż 100');

/**
 * A single grade band. Colors are derived from the band position on the
 * client, so they are intentionally absent from the wire format.
 */
export const gradeBandCreateSchema = z.object({
  name: z.string('Nazwa oceny jest wymagana').min(1, 'Nazwa oceny jest wymagana').max(20, 'Nazwa oceny jest zbyt długa (max 20 znaków)').trim(),
  minPercent: percentSchema,
  maxPercent: percentSchema,
});

interface GradeBandBounds {
  minPercent: number;
  maxPercent: number;
}

/**
 * Bands must partition 0-100% without gaps or overlaps: ascending, first min
 * 0, last max 100, each min equal to the previous max + 1.
 */
function validateBandContinuity(bands: GradeBandBounds[], ctx: z.RefinementCtx): void {
  bands.forEach((band, index) => {
    const previous: GradeBandBounds | undefined = bands[index - 1];
    if (band.minPercent > band.maxPercent) {
      ctx.addIssue({
        code: 'custom',
        message: 'Min % nie może być większy niż Max %',
        path: [index, 'minPercent'],
      });
    }
    if (index === 0 && band.minPercent !== 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Pierwszy przedział musi zaczynać się od 0%',
        path: [index, 'minPercent'],
      });
    }
    if (index === bands.length - 1 && band.maxPercent !== 100) {
      ctx.addIssue({
        code: 'custom',
        message: 'Ostatni przedział musi kończyć się na 100%',
        path: [index, 'maxPercent'],
      });
    }
    if (previous && band.minPercent !== previous.maxPercent + 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Przedziały muszą być ciągłe (bez przerw i nakładania)',
        path: [index, 'minPercent'],
      });
    }
  });
}

export const gradeSchemeCreateSchema = z.object({
  name: z.string('Nazwa schematu jest wymagana').min(1, 'Nazwa schematu jest wymagana').max(50, 'Nazwa schematu jest zbyt długa (max 50 znaków)').trim(),
  bands: z.array(gradeBandCreateSchema).min(2, 'Schemat musi zawierać co najmniej dwie oceny').superRefine(validateBandContinuity),
});

export const gradeSchemeEditSchema = gradeSchemeCreateSchema.extend({
  id: z.number(),
});

export const gradeScaleFormSchema = z.object({
  schemes: z.array(gradeSchemeCreateSchema).min(1, 'Wymagany jest co najmniej jeden schemat'),
});

/**
 * Wire format stored on a quiz/assignment. Thresholds map a grade label to the
 * minimum percentage that earns it; the highest threshold not exceeding the
 * score wins. Only percentage grading is supported, so the basis is implicit —
 * the backend validator rejects any key other than `thresholds`.
 */
export const gradeMappingSchema = z
  .object({
    thresholds: z.record(
      z.string().min(1).max(25),
      z.number('Próg jest wymagany').int('Próg musi być liczbą całkowitą').min(0, 'Próg nie może być mniejszy niż 0').max(100, 'Próg nie może być większy niż 100')
    ),
  })
  .refine((mapping) => Object.keys(mapping.thresholds).length >= 2, {
    message: 'Mapowanie musi zawierać co najmniej dwa progi',
    path: ['thresholds'],
  })
  .refine((mapping) => Object.values(mapping.thresholds).includes(0), {
    message: 'Najniższa ocena musi zaczynać się od 0%',
    path: ['thresholds'],
  });

/**
 * Bidirectional bridge between editor bands and the stored mapping. `decode`
 * (bands → mapping) takes each band's lower bound as its threshold; `encode`
 * (mapping → bands) rebuilds contiguous bands, each ending just below the next
 * threshold. Use via `gradeMappingCodec.decode/encode` (and the `safe*`
 * variants) — both sides are validated and percentages coerced.
 */
export const gradeMappingCodec = z.codec(z.array(gradeBandCreateSchema), gradeMappingSchema, {
  decode: (bands) => ({
    thresholds: Object.fromEntries(bands.map((band) => [band.name, band.minPercent])),
  }),
  encode: (mapping) => {
    const entries = Object.entries(mapping.thresholds)
      .map(([name, value]) => [name, Number(value)] as const)
      .sort(([, left], [, right]) => left - right);
    return entries.map(([name, threshold], index) => {
      const nextThreshold: number | undefined = entries[index + 1]?.[1];
      return {
        name,
        minPercent: threshold,
        maxPercent: nextThreshold === undefined ? 100 : Math.max(threshold, nextThreshold - 1),
      };
    });
  },
});

export const gradeBandPublicSchema = gradeBandCreateSchema.extend({
  id: z.number(),
});

export const gradeSchemePublicSchema = z.object({
  id: z.number(),
  name: z.string(),
  bands: z.array(gradeBandPublicSchema).min(2, 'Schemat musi zawierać co najmniej dwie oceny').superRefine(validateBandContinuity),
});

export type GradeBandCreateSchema = Infer<typeof gradeBandCreateSchema>;
export type GradeSchemeCreateSchema = Infer<typeof gradeSchemeCreateSchema>;
export type GradeSchemeEditSchema = Infer<typeof gradeSchemeEditSchema>;
export type GradeScaleFormSchema = Infer<typeof gradeScaleFormSchema>;
export type GradeBandPublicSchema = Infer<typeof gradeBandPublicSchema>;
export type GradeSchemePublicSchema = Infer<typeof gradeSchemePublicSchema>;
export type GradeMappingSchema = Infer<typeof gradeMappingSchema>;
